import React from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import MessageIcon from '@mui/icons-material/Message';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuthentication, useFavorites, useNotifications } from '../../store/AppContext';
import { DESIGN_TOKENS } from '../../styles/theme';
import { EmptyState, ErrorDisplay, ListingCard, LoadingSpinner } from '../../components/common';
import { useFetch, useUserProfile } from '../../hooks';
import {
  fetchListingsByOwner2,
  fetchReceivedApplications,
  fetchSentApplications,
  sendApplicationMessage,
} from '../../api/services';
import type {
  ApplicationMessageSenderType,
  Listing,
  StrapiListResponse,
  SubmittedApplication,
} from '../../types';
import { pl } from '../../i18n';
import { ApplicationConversation, CompanyApplicationsSection, getApplicationConversation } from './applications';
import { resolveAssetUrl } from '../../utils';

const getAccountNavigationItems = (userType?: string) => {
  const commonItems = [
    { label: pl.account.dashboard, path: '/account', icon: <DashboardIcon fontSize="small" /> },
    { label: pl.account.favourites, path: '/account/favourites', icon: <BookmarkIcon fontSize="small" /> },
  ];

  const pilotItems = [
    { label: pl.account.myListings, path: '/account/my-listings', icon: <ListAltIcon fontSize="small" /> },
    { label: pl.account.sentApplications, path: '/account/sent-applications', icon: <SendIcon fontSize="small" /> },
    { label: pl.account.myOrders, path: '/account/my-orders', icon: <ListAltIcon fontSize="small" /> },
    { label: pl.account.preferences, path: '/account/preferences', icon: <LocationOnIcon fontSize="small" /> },
  ];

  const companyItems = [
    { label: pl.account.listingsAndOffers, path: '/account/listings-and-offers', icon: <ListAltIcon fontSize="small" /> },
  ];

  const bottomItems = [
    { label: pl.account.messages, path: '/account/messages', icon: <MessageIcon fontSize="small" /> },
    { label: pl.account.settings, path: '/account/settings', icon: <SettingsIcon fontSize="small" /> },
  ];

  if (userType === 'PILOT_B2B_COMPANY') {
    return [...commonItems, ...pilotItems, ...bottomItems];
  }

  if (userType === 'TRANSPORT_COMPANY') {
    return [...commonItems, ...companyItems, ...bottomItems];
  }

  return [...commonItems, ...bottomItems];
};

function isAccountNavItemActive(currentPath: string, navPath: string) {
  if (navPath === '/account') {
    return currentPath === '/account';
  }

  if (navPath === '/account/listings-and-offers') {
    return (
      currentPath.startsWith('/account/listings-and-offers') ||
      currentPath.startsWith('/account/my-listings') ||
      currentPath.startsWith('/account/received-applications') ||
      currentPath.startsWith('/account/orders')
    );
  }

  return currentPath.startsWith(navPath);
}

export interface AccountLayoutProps {
  children: React.ReactNode;
  pageTitle: string;
}

export function AccountLayout({ children, pageTitle }: AccountLayoutProps) {
  const { isAuthenticated, currentUser, isLoading } = useAuthentication();
  const location = useLocation();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading || (isAuthenticated && !currentUser)) {
    return <LoadingSpinner message={pl.auth.checkingAccount} fullHeight />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isOnboardingPage =
    location.pathname === '/first-steps' ||
    location.pathname.startsWith('/onboarding/');

  if (!currentUser?.membership && !isOnboardingPage) {
    return <Navigate to="/first-steps" replace />;
  }

  const userInitial = currentUser?.username?.[0]?.toUpperCase() ?? 'U';
  const userLogoUrl = resolveAssetUrl(
    currentUser?.membership?.company_transport?.company_information?.brand_graphics?.logo?.url ??
      currentUser?.membership?.pilot?.company_information?.brand_graphics?.logo?.url,
  );
  const navItems = getAccountNavigationItems(currentUser?.membership?.USER_TYPE);

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '80vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {!isMobileView && (
            <Grid item md={3} lg={2.5}>
              <Card sx={{ position: 'sticky', top: 80 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                      src={userLogoUrl}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: userLogoUrl ? DESIGN_TOKENS.colors.surface : DESIGN_TOKENS.colors.primary,
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                        '& .MuiAvatar-img': {
                          objectFit: 'contain',
                          padding: userLogoUrl ? '6px' : 0,
                        },
                      }}
                    >
                      {userInitial}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {currentUser?.username ?? 'Użytkownik'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {currentUser?.email ?? ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <List dense disablePadding>
                    {navItems.map((navItem) => {
                      const isActive = isAccountNavItemActive(location.pathname, navItem.path);
                      return (
                        <ListItem key={navItem.path} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            component={RouterLink}
                            to={navItem.path}
                            selected={isActive}
                            sx={{
                              borderRadius: '8px',
                              '&.Mui-selected': {
                                backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                                color: DESIGN_TOKENS.colors.primary,
                                '& .MuiListItemIcon-root': { color: DESIGN_TOKENS.colors.primary },
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>{navItem.icon}</ListItemIcon>
                            <ListItemText
                              primary={navItem.label}
                              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={9} lg={9.5}>
            {isMobileView && (
              <Box sx={{ mb: 3, overflowX: 'auto' }}>
                <Tabs
                  value={Math.max(0, navItems.findIndex((item) => isAccountNavItemActive(location.pathname, item.path)))}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {navItems.map((navItem) => (
                    <Tab
                      key={navItem.path}
                      label={navItem.label}
                      component={RouterLink}
                      to={navItem.path}
                      icon={navItem.icon}
                      iconPosition="start"
                      sx={{ minHeight: 48, textTransform: 'none', fontWeight: 500 }}
                    />
                  ))}
                </Tabs>
              </Box>
            )}

            <Typography variant="h3" sx={{ mb: 3 }}>
              {pageTitle}
            </Typography>

            {children}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export function AccountDashboardSection() {
  const { currentUser } = useAuthentication();
  const { favorites } = useFavorites();
  const isTransportCompany = currentUser?.membership?.USER_TYPE === 'TRANSPORT_COMPANY';

  const { data: profile, isLoading: isProfileLoading, error: profileError, refetch: refetchProfile } = useUserProfile();
  const companyDocumentId = profile?.type === 'COMPANY' ? profile.documentId : '';
  const { data: listingsData, isLoading: isListingsLoading, error: listingsError, refetch: refetchListings } = useFetch<StrapiListResponse<Listing>>(
    () => fetchListingsByOwner2(companyDocumentId),
    [companyDocumentId],
    { enabled: !!companyDocumentId }
  );

  const listingsCount = listingsData?.data?.length ?? 0;

  if (isProfileLoading || isListingsLoading) {
    return <LoadingSpinner message="Ładowanie danych pulpitu..." />;
  }

  if (profileError || listingsError) {
    return (
      <ErrorDisplay
        message={profileError || listingsError || 'Wystąpił błąd'}
        onRetry={() => {
          if (profileError) refetchProfile();
          if (listingsError) refetchListings();
        }}
      />
    );
  }

  return (
    <Grid container spacing={3}>
      {[
        { label: 'Aktywne ogłoszenia', value: listingsCount.toString() },
        { label: 'Ulubione', value: favorites.length.toString() },
        { label: 'Nieprzeczytane wiadomości', value: '0' },
      ].map((statItem) => (
        <Grid item xs={12} sm={4} key={statItem.label}>
          <Card>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" color="primary" fontWeight={700}>
                {statItem.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statItem.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Witaj w QPRO Connect, {currentUser?.username}!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Z tego miejsca zarządzasz swoimi ogłoszeniami, wiadomościami i profilem.
              Poniżej znajdziesz skróty do najważniejszych sekcji.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to={isTransportCompany ? '/account/listings-and-offers' : '/account/my-listings'}
                variant="contained"
                size="small"
              >
                Moje ogłoszenia
              </Button>
              <Button component={RouterLink} to="/listings" variant="outlined" size="small">
                Przeglądaj ogłoszenia
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

interface MyListingsContentProps {
  embedded?: boolean;
}

export function MyListingsSection({ embedded = false }: MyListingsContentProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuthentication();
  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';

  const { data: profile, isLoading: isProfileLoading } = useUserProfile();
  const companyDocumentId = profile?.type === 'COMPANY' ? profile.documentId : '';
  const { data: listingsData, isLoading: isListingsLoading, error, refetch } = useFetch<StrapiListResponse<Listing>>(
    () => fetchListingsByOwner2(companyDocumentId),
    [companyDocumentId],
    { enabled: !!companyDocumentId }
  );

  const listings = listingsData?.data ?? [];

  const content = (
    <>
      {!isPilot && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button variant="contained" startIcon={<ListAltIcon />} onClick={() => navigate('/account/my-listings/add')}>
            Dodaj ogłoszenie
          </Button>
        </Box>
      )}

      {isProfileLoading || isListingsLoading ? (
        <LoadingSpinner message="Ładowanie ogłoszeń..." />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={refetch} />
      ) : listings.length > 0 ? (
        <Grid container spacing={3}>
          {listings.map((listing) => (
            <Grid item xs={12} sm={6} lg={4} key={listing.id}>
              <ListingCard listing={listing} />
              {!isPilot && (
                <Button
                  component={RouterLink}
                  to={`/account/received-applications/${listing.documentId}`}
                  startIcon={<RequestPageIcon />}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1.5 }}
                >
                  Zobacz nadesłane oferty
                </Button>
              )}
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          title="Brak ogłoszeń"
          description={pl.account.noListingsDescription}
          actionLabel="Dodaj ogłoszenie"
          onAction={() => navigate('/account/my-listings/add')}
        />
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return <AccountLayout pageTitle="Moje ogłoszenia">{content}</AccountLayout>;
}

export function FavouritesSection() {
  const navigate = useNavigate();
  const { favorites } = useFavorites();

  return favorites.length > 0 ? (
    <Grid container spacing={3}>
      {favorites.map((fav) => {
        const listing = fav.listing_transport_company;
        if (!listing || !listing.basic_information) return null;

        return (
          <Grid item xs={12} sm={6} lg={4} key={fav.id}>
            <ListingCard listing={listing} />
          </Grid>
        );
      })}
    </Grid>
  ) : (
    <EmptyState
      title="Brak zapisanych ogłoszeń"
      description={pl.account.noFavouritesDescription}
      actionLabel="Przeglądaj ogłoszenia"
      onAction={() => navigate('/listings')}
    />
  );
}

export function MessagesSection() {
  const { currentUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile, isLoading: isProfileLoading, error: profileError, refetch: refetchProfile } = useUserProfile();
  const [selectedListingDocumentId, setSelectedListingDocumentId] = React.useState('ALL');
  const [replyDrafts, setReplyDrafts] = React.useState<Record<string, string>>({});
  const [sendingMessageId, setSendingMessageId] = React.useState<string | null>(null);

  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';
  const isCompany = currentUser?.membership?.USER_TYPE === 'TRANSPORT_COMPANY';
  const currentSenderType: ApplicationMessageSenderType = isCompany ? 'COMPANY' : 'PILOT';

  const {
    data: applications,
    isLoading,
    error,
    refetch,
  } = useFetch<SubmittedApplication[]>(
    async () => {
      if (!currentUser?.membership) return [];

      if (currentUser.membership.USER_TYPE === 'PILOT_B2B_COMPANY') {
        const response = await fetchSentApplications(currentUser.membership.pilot?.documentId ?? '');
        return response.data;
      }

      if (currentUser.membership.USER_TYPE === 'TRANSPORT_COMPANY') {
        const [openResponse, closedResponse] = await Promise.all([
          fetchReceivedApplications(currentUser.membership.company_transport?.documentId ?? '', undefined, 'OPEN'),
          fetchReceivedApplications(currentUser.membership.company_transport?.documentId ?? '', undefined, 'CLOSED'),
        ]);

        const merged = [...openResponse.data, ...closedResponse.data];
        const deduped = new Map<string, SubmittedApplication>();

        merged.forEach((application) => {
          const key = application.documentId ?? String(application.id ?? '');
          if (key) {
            deduped.set(key, application);
          }
        });

        return Array.from(deduped.values()).sort((first, second) => {
          const firstTimestamp = getApplicationConversation(first).at(-1)?.createdAt ?? first.createdAt ?? '';
          const secondTimestamp = getApplicationConversation(second).at(-1)?.createdAt ?? second.createdAt ?? '';
          return new Date(secondTimestamp).getTime() - new Date(firstTimestamp).getTime();
        });
      }

      return [];
    },
    [currentUser?.membership?.USER_TYPE, currentUser?.membership?.pilot?.documentId, currentUser?.membership?.company_transport?.documentId],
    { enabled: Boolean(currentUser?.membership && (isPilot || isCompany)) }
  );

  const listingOptions = React.useMemo(() => {
    const options = new Map<string, string>();

    applications?.forEach((application) => {
      const listing = application.listing && typeof application.listing === 'object' ? application.listing : undefined;
      const listingDocumentId = listing?.documentId ?? (typeof application.listing === 'string' ? application.listing : undefined);
      if (!listingDocumentId) return;

      options.set(listingDocumentId, listing?.basic_information?.title ?? 'Ogłoszenie bez tytułu');
    });

    return Array.from(options.entries()).map(([documentId, title]) => ({ documentId, title }));
  }, [applications]);

  const filteredApplications = React.useMemo(() => {
    const source = applications ?? [];

    return source.filter((application) => {
      if (selectedListingDocumentId === 'ALL') return true;

      const listing = application.listing && typeof application.listing === 'object' ? application.listing : undefined;
      const listingDocumentId = listing?.documentId ?? (typeof application.listing === 'string' ? application.listing : undefined);
      return listingDocumentId === selectedListingDocumentId;
    });
  }, [applications, selectedListingDocumentId]);

  const handleDraftChange = React.useCallback((applicationDocumentId: string, value: string) => {
    setReplyDrafts((previous) => ({
      ...previous,
      [applicationDocumentId]: value,
    }));
  }, []);

  const handleSendMessage = React.useCallback(async (application: SubmittedApplication) => {
    if (!application.documentId) return;

    const applicationDocumentId = application.documentId;
    const draft = replyDrafts[applicationDocumentId]?.trim();
    if (!draft) return;

    setSendingMessageId(applicationDocumentId);
    try {
      await sendApplicationMessage(applicationDocumentId, draft);
      setReplyDrafts((previous) => ({
        ...previous,
        [applicationDocumentId]: '',
      }));
      showNotification('Wiadomość została wysłana.', 'success');
      refetch();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się wysłać wiadomości.', 'error');
    } finally {
      setSendingMessageId(null);
    }
  }, [refetch, replyDrafts, showNotification]);

  if (isProfileLoading || isLoading) {
    return <LoadingSpinner message="Ładowanie wiadomości..." />;
  }

  if (profileError || error) {
    return (
      <ErrorDisplay
        message={profileError || error || 'Wystąpił błąd'}
        onRetry={() => {
          if (profileError) refetchProfile();
          if (error) refetch();
        }}
      />
    );
  }

  if (!profile || (!isPilot && !isCompany)) {
    return (
      <EmptyState
        title={pl.common.noAccess}
        description={pl.account.noMessagesDescription}
      />
    );
  }

  if (!filteredApplications.length) {
    return (
      <>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Filtr konwersacji
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Wybierz ogłoszenie, aby zawęzić listę rozmów.
                </Typography>
              </Box>
              <TextField
                select
                label="Ogłoszenie"
                value={selectedListingDocumentId}
                onChange={(event) => setSelectedListingDocumentId(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 320 } }}
              >
                <MenuItem value="ALL">Wszystkie ogłoszenia</MenuItem>
                {listingOptions.map((option) => (
                  <MenuItem key={option.documentId} value={option.documentId}>
                    {option.title}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </CardContent>
        </Card>
        <EmptyState
          title="Brak wiadomości"
          description={
            selectedListingDocumentId === 'ALL'
              ? pl.account.noMessagesDescription
              : 'Nie ma jeszcze wiadomości dla wybranego ogłoszenia.'
          }
        />
      </>
    );
  }

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Wszystkie wiadomości
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Przeglądaj wszystkie konwersacje i filtruj je po ogłoszeniu.
              </Typography>
            </Box>
            <TextField
              select
              label="Ogłoszenie"
              value={selectedListingDocumentId}
              onChange={(event) => setSelectedListingDocumentId(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 320 } }}
            >
              <MenuItem value="ALL">Wszystkie ogłoszenia</MenuItem>
              {listingOptions.map((option) => (
                <MenuItem key={option.documentId} value={option.documentId}>
                  {option.title}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {filteredApplications.map((application) => {
          const listing = application.listing && typeof application.listing === 'object' ? application.listing : undefined;
          const lastMessage = getApplicationConversation(application).at(-1);
          const counterpartLabel = isCompany
            ? `${application.pilot_b2b && typeof application.pilot_b2b === 'object'
              ? `${application.pilot_b2b.personal_data?.name ?? ''} ${application.pilot_b2b.personal_data?.surname ?? ''}`.trim() || 'Pilot'
              : 'Pilot'}`
            : listing?.owner?.company_information?.company_data?.company_name ?? 'Firma transportowa';

          return (
            <Grid item xs={12} key={application.documentId ?? application.id}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                    <Box sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        OGŁOSZENIE
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        {listing?.basic_information?.title ?? 'Ogłoszenie bez tytułu'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                        Rozmowa z: {counterpartLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                        Status aplikacji: {application.status ?? 'PENDING'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ostatnia aktywność:{' '}
                        {lastMessage?.createdAt
                          ? new Date(lastMessage.createdAt).toLocaleString('pl-PL')
                          : application.createdAt
                            ? new Date(application.createdAt).toLocaleString('pl-PL')
                            : 'Nieznana data'}
                      </Typography>
                      {listing?.documentId && (
                        <Button
                          component={RouterLink}
                          to={`/listings/${listing.documentId}`}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 2 }}
                        >
                          Otwórz ogłoszenie
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <ApplicationConversation
                        application={application}
                        currentSenderType={currentSenderType}
                        draft={application.documentId ? (replyDrafts[application.documentId] ?? '') : ''}
                        onDraftChange={(value) => {
                          if (!application.documentId) return;
                          handleDraftChange(application.documentId, value);
                        }}
                        onSend={() => handleSendMessage(application)}
                        isSending={sendingMessageId === application.documentId}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

export function CompanyListingsOffersSection() {
  const { currentUser, isLoading } = useAuthentication();
  const [searchParams, setSearchParams] = useSearchParams();

  if (isLoading) {
    return <LoadingSpinner message={pl.auth.checkingAccount} fullHeight />;
  }

  if (currentUser?.membership?.USER_TYPE !== 'TRANSPORT_COMPANY') {
    return (
      <AccountLayout pageTitle={pl.account.listingsAndOffers}>
        <EmptyState
          title={pl.common.noAccess}
          description={pl.account.receivedCompanyOnly}
        />
      </AccountLayout>
    );
  }

  const currentTab = searchParams.get('tab') ?? 'listings';
  const allowedTabs = ['listings', 'received', 'orders'];
  const normalizedTab = allowedTabs.includes(currentTab) ? currentTab : 'listings';
  const activeTab = normalizedTab === 'listings' ? 0 : normalizedTab === 'received' ? 1 : 2;

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: number) => {
    const tabValue = nextTab === 0 ? 'listings' : nextTab === 1 ? 'received' : 'orders';
    setSearchParams({ tab: tabValue });
  };

  return (
    <AccountLayout pageTitle={pl.account.listingsAndOffers}>
      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label={pl.account.myListings} />
          <Tab label={pl.account.receivedApplications} />
          <Tab label={pl.account.orders} />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <MyListingsSection embedded />
      ) : activeTab === 1 ? (
        <CompanyApplicationsSection
          listingStatus="OPEN"
          emptyTitle="Brak nadesłanych ofert"
          emptyDescription="Nikt jeszcze nie zaaplikował na Twoje otwarte ogłoszenia."
          detailBasePath="/account/received-applications"
          backPath="/account/listings-and-offers?tab=received"
          showStartAction
        />
      ) : (
        <CompanyApplicationsSection
          listingStatus="CLOSED"
          emptyTitle="Brak zleceń"
          emptyDescription="Nie masz jeszcze zamkniętych ogłoszeń, które zostały przekształcone w zlecenia."
          detailBasePath="/account/orders"
          backPath="/account/listings-and-offers?tab=orders"
          showStartAction={false}
        />
      )}
    </AccountLayout>
  );
}
